import { Dispatch, SetStateAction, MutableRefObject, useCallback, useEffect, useRef } from "react";
import { ChapterView } from "./studioAssetModel";
import { StudioState } from "./studioModel";
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { cacheStudioLayout, selectStudioCache } from '../../store/studioCacheSlice';

const PANELS_KEY = "fable-studio-panels-v1";
const TREE_KEY = "fable-studio-tree-v1";
const LEGACY_SECTIONS_KEY = "fable-studio-tree-sections-v1";

export type StudioTreeSection = "pinned" | "stories" | "completed";
export type StudioMobileDrawer = "projects" | "steps" | null;

export type StudioProjectListSnapshot = {
  query: string;
  searchQuery?: string;
  limits: Record<StudioTreeSection, number>;
  searchCollapsedSections: StudioTreeSection[];
  projectId: string;
  chapterId: string;
  scrollTop: number;
};

export type StudioPanels = {
  projects: boolean;
  preview: boolean;
  view: ChapterView;
  ratio: number;
  // Follow the responsive default until the user makes their own choice.
  stepsExpanded: boolean | null;
  mobileDrawer: StudioMobileDrawer;
};

export type StudioTree = {
  expandedProjects: string[];
  expandedChapters: string[];
  collapsedSections: StudioTreeSection[];
};

function readStorage(key: string): unknown {
  try {
    return JSON.parse(localStorage.getItem(key) || "null");
  } catch {
    return null;
  }
}

function readObject(key: string): Record<string, unknown> {
  const value = readStorage(key);
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function saveStorage(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Layout controls remain usable when browser storage is unavailable.
  }
}

export function readStudioPanels(): StudioPanels {
  const saved = readObject(PANELS_KEY);
  return {
    projects: saved.projects !== false,
    preview: saved.preview !== false,
    view: saved.view === "assets" ? "assets" : "chat",
    ratio: typeof saved.ratio === "number" && Number.isFinite(saved.ratio)
      ? Math.max(0.28, Math.min(0.65, saved.ratio))
      : 0.49,
    stepsExpanded: typeof saved.stepsExpanded === "boolean" ? saved.stepsExpanded : null,
    // Mobile sheets have their own state, independent of desktop sidebars.
    mobileDrawer: saved.mobileDrawer === null || saved.mobileDrawer === "steps"
      ? saved.mobileDrawer
      : saved.mobileDrawer === "projects" ? "projects" : null,
  };
}

function readTree(state: StudioState): StudioTree {
  const saved = readObject(TREE_KEY);
  const projects = state.projects.map((p) => p.id);
  const chapters = state.projects.flatMap((p) => p.chapters.map((c) => c.id));
  const validIds = (value: unknown, ids: string[], fallback: string[]) =>
    Array.isArray(value)
      ? value.filter((id): id is string => typeof id === "string" && (ids.includes(id) || id.startsWith("saved-")))
      : fallback;
  const sections = saved.collapsedSections ?? readStorage(LEGACY_SECTIONS_KEY);
  return {
    expandedProjects: validIds(saved.expandedProjects, projects, projects),
    expandedChapters: validIds(saved.expandedChapters, chapters, chapters.includes(state.chapterId) ? [state.chapterId] : []),
    collapsedSections: Array.isArray(sections)
      ? sections.filter((id): id is StudioTreeSection => id === "pinned" || id === "stories" || id === "completed")
      : [],
  };
}

/** Redux owns disclosure state; mounted drawers and loading shells share it. */
export function useStudioPreferences(state: StudioState): StudioPreferences {
  const ownerId = useAppSelector(state => state.app.userId);
  const cached = useAppSelector(state => selectStudioCache(state)?.data.layout);
  const dispatch = useAppDispatch();
  const defaults = useRef<{ ownerId: typeof ownerId; panels: StudioPanels; tree: StudioTree } | null>(null);
  if (!defaults.current || defaults.current.ownerId !== ownerId) {
    defaults.current = { ownerId, panels: readStudioPanels(), tree: readTree(state) };
  }
  const initial = defaults.current;
  const panels = cached?.panels || initial.panels;
  const tree = cached?.tree || initial.tree;
  const projectList = useRef<StudioProjectListSnapshot | null>(cached?.list ? { ...cached.list } : null);

  useEffect(() => saveStorage(PANELS_KEY, panels), [panels]);
  useEffect(() => {
    if (!ownerId) return;
    dispatch((send, getState) => {
      if (getState().app.userId !== ownerId) return;
      const current = selectStudioCache(getState())?.data.layout;
      // Initialize once. A loading shell must never overwrite a mounted drawer.
      if (!current?.panels || !current?.tree) send(cacheStudioLayout({ ownerId,
        ...(!current?.panels ? { panels: initial.panels } : {}),
        ...(!current?.tree ? { tree: initial.tree } : {}),
      }));
    });
    try { localStorage.removeItem(TREE_KEY); } catch {}
  }, [ownerId, dispatch, initial]);

  const setPanels = useCallback<Dispatch<SetStateAction<StudioPanels>>>(update => {
    if (!ownerId) return;
    dispatch((send, getState) => {
      if (getState().app.userId !== ownerId) return;
      const current = selectStudioCache(getState())?.data.layout.panels || initial.panels;
      const next = typeof update === 'function' ? update(current) : update;
      if (next !== current) send(cacheStudioLayout({ ownerId, panels: next }));
    });
  }, [ownerId, dispatch, initial]);
  const setTree = useCallback<Dispatch<SetStateAction<StudioTree>>>(update => {
    if (!ownerId) return;
    dispatch((send, getState) => {
      if (getState().app.userId !== ownerId) return;
      // Resolve functional updates against the latest store, not a render's copy.
      const current = selectStudioCache(getState())?.data.layout.tree || initial.tree;
      const next = typeof update === 'function' ? update(current) : update;
      if (next !== current) send(cacheStudioLayout({ ownerId, tree: next }));
    });
  }, [ownerId, dispatch, initial]);

  // Selection, background refreshes and section changes never alter disclosure.
  return { panels, setPanels, tree, setTree, projectList };
}

export type StudioPreferences = {
  panels: StudioPanels;
  setPanels: Dispatch<SetStateAction<StudioPanels>>;
  tree: StudioTree;
  setTree: Dispatch<SetStateAction<StudioTree>>;
  projectList: MutableRefObject<StudioProjectListSnapshot | null>;
};
